import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  HomeIcon,
  LinkIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  MegaphoneIcon,
  CreditCardIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/lib/api';
import clsx from 'clsx';

const affiliateNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Links', href: '/links', icon: LinkIcon },
  { name: 'Commissions', href: '/commissions', icon: CurrencyDollarIcon },
  { name: 'Payouts', href: '/payouts', icon: BanknotesIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

const orgNavigation = [
  { name: 'Dashboard', href: '/org', icon: HomeIcon },
  { name: 'Affiliates', href: '/org/affiliates', icon: UserGroupIcon },
  { name: 'Campaigns', href: '/org/campaigns', icon: MegaphoneIcon },
  { name: 'Commissions', href: '/org/commissions', icon: CurrencyDollarIcon },
  { name: 'Payouts', href: '/org/payouts', icon: BanknotesIcon },
  { name: 'Integration', href: '/org/integration', icon: CodeBracketIcon },
  { name: 'Billing', href: '/org/billing', icon: CreditCardIcon },
  { name: 'Settings', href: '/org/settings', icon: Cog6ToothIcon },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, currentOrganization, logout } = useAuthStore();
  const navigate = useNavigate();

  const navigation = user?.role === 'org_admin' ? orgNavigation : affiliateNavigation;
  const dashboardTitle = user?.role === 'org_admin' ? 'Organization Dashboard' : 'Affiliate Dashboard';

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors - still logout locally
    }
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-light-gray">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-charcoal/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-[260px] flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-charcoal px-6 pb-4 dashboard-scroll">
                  <div className="flex h-16 shrink-0 items-center justify-between">
                    <Link to="/" className="font-display text-xl font-bold text-warm-white tracking-tight">
                      venndela
                    </Link>
                    <button
                      type="button"
                      className="text-tan hover:text-warm-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <NavLink
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                  clsx(
                                    'nav-link',
                                    isActive ? 'nav-link-active' : 'nav-link-inactive'
                                  )
                                }
                              >
                                <item.icon className="h-5 w-5 shrink-0" />
                                {item.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                      <li className="mt-auto">
                        <button
                          onClick={() => {
                            setSidebarOpen(false);
                            handleLogout();
                          }}
                          className="nav-link nav-link-inactive w-full text-rust hover:text-rust-light hover:bg-rust/10"
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" />
                          Logout
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-[260px] lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-charcoal px-6 pb-4 dashboard-scroll">
          <div className="flex h-16 shrink-0 items-center">
            <Link to="/" className="font-display text-xl font-bold text-warm-white tracking-tight">
              venndela
            </Link>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.href}
                        end={item.href === '/dashboard' || item.href === '/org'}
                        className={({ isActive }) =>
                          clsx(
                            'nav-link',
                            isActive ? 'nav-link-active' : 'nav-link-inactive'
                          )
                        }
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Organization info for org_admin */}
              {user?.role === 'org_admin' && currentOrganization && (
                <li className="mt-auto">
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-xs text-tan">Organization</p>
                    <p className="text-sm font-medium text-warm-white truncate">
                      {currentOrganization.name}
                    </p>
                  </div>
                </li>
              )}

              <li className={user?.role !== 'org_admin' ? 'mt-auto' : ''}>
                {/* User info */}
                <div className="flex items-center gap-3 px-2 py-3 mb-3 border-t border-white/10 pt-4">
                  <div className="h-10 w-10 rounded-full bg-rust flex items-center justify-center">
                    <span className="text-sm font-semibold text-warm-white">
                      {user?.firstName?.[0]}
                      {user?.lastName?.[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-warm-white truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-tan truncate">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="nav-link nav-link-inactive w-full text-rust hover:text-rust-light hover:bg-rust/10"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" />
                  Logout
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-[260px]">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-tan/20 bg-warm-white px-4 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-charcoal lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1 items-center">
              <h1 className="font-display text-lg font-bold text-charcoal">{dashboardTitle}</h1>
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden sm:flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-charcoal">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-charcoal/60">{user?.email}</p>
                </div>
                <div className="h-9 w-9 rounded-full bg-rust flex items-center justify-center">
                  <span className="text-sm font-medium text-warm-white">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
